// Helpers
import { formatCurrency, transformDateToLocalDateString } from "helpers";

// Servisni sloj
import { FleetVehicleServiceRow } from "services/api/fleet/vehicles/services/types";

// UI
import Badge from "ui/components/Badge/Badge";
import { BadgeProps } from "ui/components/Badge/types";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import VehicleCell from "ui/components/Table/TableSearch/components/Cells/VehicleCell";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";

/** Boja statusa dolazi s backenda kao naziv palete, isto kao kod šteta. */
const STATUS_BADGE_VARIANT: Record<string, BadgeProps["variant"]> = {
  orange: "warning",
  blue: "info",
  green: "success",
  slate: "secondary",
};

/** Hrvatska množina: 1 šteta, 2-4 štete, 5+ šteta. */
const damageCountLabel = (count: number): string => {
  const lastTwo = count % 100;
  const last = count % 10;

  if (lastTwo >= 11 && lastTwo <= 14) return "šteta";
  if (last === 1) return "šteta";
  if (last >= 2 && last <= 4) return "štete";

  return "šteta";
};

export const FLEET_VEHICLE_SERVICE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onDeleteClick: (event: React.MouseEvent<HTMLSpanElement>, id: string) => void,
  /** Zajednička pretraga ide kroz sva vozila, pa redak nosi i karticu vozila. */
  showVehicle?: boolean,
  onVehicleClick?: (event: React.MouseEvent, vehicleId: string) => void
): Column[] => [
  {
    title: "",
    key: "service_icon",
    alwaysVisible: true,
    field: "",
    type: ColumnTypes.custom,
    // Nalozi su sve jedne vrste, pa ide ikona servisa umjesto inicijala kao kod premještaja.
    render: () => <TableIcon icon="/media/icons/service.svg" />,
  },
  {
    title: "admin.fleet.vehicle.service.search.name",
    key: "service_name",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => (
      <div className="min-w-0">
        <div className="font-medium text-slate-900">{row.name}</div>
        {row.description && (
          <div className="text-sm text-slate-500 max-w-[300px] overflow-hidden text-ellipsis line-clamp-2">
            {row.description}
          </div>
        )}
      </div>
    ),
  },
  // Na tabu vozila je kolona suvišna — ondje je vozilo već poznato.
  ...(showVehicle
    ? [
        {
          title: "Vozilo",
          key: "service_vehicle",
          field: "",
          type: ColumnTypes.custom,
          render: (row: FleetVehicleServiceRow) => {
            if (!row.vehicle) return null;

            return (
              <VehicleCell
                vehicle={row.vehicle}
                onClick={(e) => row.vehicle?.id && onVehicleClick?.(e, row.vehicle.id)}
              />
            );
          },
        } as Column,
      ]
    : []),
  {
    title: "admin.fleet.vehicle.service.search.contact",
    key: "service_contact",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => (
      <div className="text-sm text-slate-700 truncate">{row.contactName ?? "-"}</div>
    ),
  },
  {
    title: "admin.fleet.vehicle.service.search.status",
    key: "service_status",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) =>
      row.vehicleServiceStatus ? (
        <Badge
          text={row.vehicleServiceStatus.name}
          variant={STATUS_BADGE_VARIANT[row.vehicleServiceStatus.color] ?? "secondary"}
          height="small"
          showDot
        />
      ) : (
        <span className="text-sm text-slate-400">-</span>
      ),
  },
  {
    title: "admin.fleet.vehicle.service.search.receiveddate",
    key: "service_receiveddate",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => (
      <div className="text-sm text-slate-900">
        {row.receivedDate ? transformDateToLocalDateString(row.receivedDate) : "-"}
      </div>
    ),
  },
  {
    title: "admin.fleet.vehicle.service.search.completeddate",
    key: "service_completeddate",
    field: "",
    type: ColumnTypes.custom,
    // Otvoren nalog nema datum preuzimanja — umjesto crtice se označi da vozilo još nije vraćeno.
    render: (row: FleetVehicleServiceRow) =>
      row.isOpen ? (
        <Badge text="Otvoren" variant="warning" height="small" showDot />
      ) : (
        <div className="text-sm text-slate-900">
          {row.completedDate ? transformDateToLocalDateString(row.completedDate) : "-"}
        </div>
      ),
  },
  {
    title: "admin.fleet.vehicle.service.search.damages",
    key: "service_damages",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => {
      const count = row.damages.length;

      if (count === 0) return <span className="text-xs text-slate-400">-</span>;

      return (
        <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          {count} {damageCountLabel(count)}
        </span>
      );
    },
  },
  {
    title: "admin.fleet.vehicle.service.search.totalexpenses",
    key: "service_totalexpenses",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => (
      <div className="text-sm font-medium text-slate-900">
        {formatCurrency.format(row.totalExpenses)}
      </div>
    ),
  },
  {
    title: "",
    field: "type",
    type: ColumnTypes.custom,
    render: (row: FleetVehicleServiceRow) => (
      <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
        <ButtonMenuItem
          startIcon="/media/icons/trash.svg"
          variant="remove"
          onClick={(e) => onDeleteClick(e, row.id)}
        >
          {t("admin.delete")}
        </ButtonMenuItem>
      </ButtonMenu>
    ),
  },
];
